import * as THREE from "three"
import Home from "./Home";
import Experience from "../../../Experience";
import { TextGeometry } from "three/examples/jsm/Addons.js";
import { isTouchOnlyDevice } from "../../../Utils/InputDevice.js";
import gsap from "gsap";

export default class Description {

    constructor(title, description, image, link) {

        this.home = new Home()
        this.experience = new Experience()
        this.resources = this.experience.resources
        this.font = this.resources.items.font2
        this.scene = this.home.scene
        this.interaction = this.home.interaction

        this.pageTitle = title
        this.pageDescription = description
        this.pageLink = link || null
        this.objects = []
        this.descriptionMaxCharactersPerLine = 28
        this.images = image

        this.primaryColor = "#c7ff0f"
        this.secondaryColor = "#000000"

        this.scrollY = 0
        this.maxScroll = 0
        this.scrollStep = 1.8

        if (this.resources.loaded === this.resources.toLoad) {
            this.setup()
        }
        else {
            this.resources.on("ready", () => {
                this.setup()
            })
        }
    }

    setup() {
        this.font = this.resources.items.font2
        if (!this.images) {
            this.images = this.resources.items.stickingTogether
        }
        if (this.images) {
            this.images.colorSpace = THREE.SRGBColorSpace
        }

        this.contentGroup = new THREE.Group()
        this.scene.add(this.contentGroup)
        this.objects.push(this.contentGroup)

        this.title()
        this.description()
        this.imagesSetup()
        if (this.pageLink) {
            this.linkPlaneSetup()
            this.setupLinkClick()
        }
        this.calculateMaxScroll()

        this.interaction.setScrollable(this)

        this.wheelHandler = (event) => {
            if (event.deltaY > 0) {
                this.scrollDown()
            } else if (event.deltaY < 0) {
                this.scrollUp()
            }
        }
        window.addEventListener('wheel', this.wheelHandler, { passive: true })
    }

    calculateMaxScroll() {
        this.contentGroup.position.y = 0
        this.contentGroup.updateMatrixWorld(true)
        const box = new THREE.Box3().setFromObject(this.contentGroup)

        const targetBottom = -7.5
        this.maxScroll = Math.max(0, targetBottom - box.min.y)
    }

    scrollUp() {
        if (this.scrollY <= 0) {
            return false
        }
        this.scrollY = Math.max(0, this.scrollY - this.scrollStep)
        this.animateScroll()
        return true
    }

    scrollDown() {
        if (this.scrollY >= this.maxScroll) {
            return false
        }
        this.scrollY = Math.min(this.maxScroll, this.scrollY + this.scrollStep)
        this.animateScroll()
        return true
    }

    animateScroll() {
        gsap.to(this.contentGroup.position, {
            y: this.scrollY,
            duration: 0.25,
            ease: "power2.out",
            overwrite: "auto"
        })
    }

    confirm() {
        this.onLinkClick()
    }

    linkPlaneSetup() {
        const isTouchDevice = isTouchOnlyDevice()
        this.selectImg = isTouchDevice ? this.resources.items.a : this.resources.items.space
        if (this.selectImg) {
            this.selectImg.colorSpace = THREE.SRGBColorSpace
        }

        this.planeSize = 3.5

        this.linkPlane = new THREE.Mesh(
            new THREE.PlaneGeometry(this.planeSize, this.planeSize),
            new THREE.MeshBasicMaterial({
                map: this.selectImg,
                transparent: true
            })
        )

        const planeOffsetX = isTouchDevice ? -1.0 : -1.3
        const textOffsetX = isTouchDevice ? 0.0 : 0.1

        this.linkPlane.position.set(planeOffsetX, 0, 0)

        // Text "to open"
        this.openText = null
        if (this.font) {
            const geometry = new TextGeometry("to open", {
                font: this.font,
                size: 0.5,
                depth: 0,
                curveSegments: 12
            })
            this.openText = new THREE.Mesh(
                geometry,
                new THREE.MeshBasicMaterial({
                    color: "#ffffff"
                })
            )
            this.openText.position.set(textOffsetX, -0.2, 0.01)
        }

        this.linkGroup = new THREE.Group()
        this.linkGroup.add(this.linkPlane)
        this.objects.push(this.linkPlane)

        if (this.openText) {
            this.linkGroup.add(this.openText)
            this.objects.push(this.openText)
        }

        this.linkGroup.position.set(6, -5.7, 0.01)
        this.contentGroup.add(this.linkGroup)
        this.objects.push(this.linkGroup)
    }

    setupLinkClick() {
        if (!this.pageLink || !this.linkGroup) return

        this.raycaster = new THREE.Raycaster()
        this.mouse = new THREE.Vector2()
        this.isHovered = false
        this.pointerDownPos = null

        const getCanvas = () => {
            return this.home.canvas || this.home.renderer?.instance?.domElement || document.querySelector("canvas.screenWebgl")
        }

        const getIntersects = (event) => {
            const canvas = getCanvas()
            if (!canvas) return []

            const rect = canvas.getBoundingClientRect()
            if (!rect.width || !rect.height) return []

            this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
            this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1

            const camera = this.home.camera?.instance
            if (!camera) return []

            this.contentGroup.updateMatrixWorld(true)
            this.raycaster.setFromCamera(this.mouse, camera)
            return this.raycaster.intersectObjects([this.linkGroup], true)
        }

        this.pointerDownHandler = (event) => {
            this.pointerDownPos = { x: event.clientX, y: event.clientY }
        }

        this.clickHandler = (event) => {
            if (this.pointerDownPos) {
                const dist = Math.hypot(event.clientX - this.pointerDownPos.x, event.clientY - this.pointerDownPos.y)
                if (dist > 10) return
            }

            const intersects = getIntersects(event)
            if (intersects.length > 0) {
                this.onLinkClick()
            }
        }

        this.pointerMoveHandler = (event) => {
            const canvas = getCanvas()
            const intersects = getIntersects(event)

            if (intersects.length > 0) {
                if (canvas) canvas.style.cursor = 'pointer'
                document.body.style.cursor = 'pointer'

                if (!this.isHovered) {
                    this.isHovered = true
                    if (this.interaction?.hoverSound) {
                        this.interaction.hoverSound.currentTime = 0
                        this.interaction.hoverSound.play().catch(() => { })
                    }
                    if (this.openText) {
                        this.openText.material.color.set(this.primaryColor)
                    }
                    gsap.to(this.linkGroup.scale, {
                        x: 1.08,
                        y: 1.08,
                        duration: 0.15,
                        ease: "power2.out",
                        overwrite: "auto"
                    })
                }
            } else {
                if (this.isHovered) {
                    this.isHovered = false
                    if (canvas) canvas.style.cursor = ''
                    document.body.style.cursor = ''
                    if (this.openText) {
                        this.openText.material.color.set("#ffffff")
                    }
                    gsap.to(this.linkGroup.scale, {
                        x: 1,
                        y: 1,
                        duration: 0.15,
                        ease: "power2.out",
                        overwrite: "auto"
                    })
                }
            }
        }

        window.addEventListener('pointerdown', this.pointerDownHandler)
        window.addEventListener('pointermove', this.pointerMoveHandler)
        window.addEventListener('click', this.clickHandler)
    }

    onLinkClick() {
        if (!this.pageLink) return

        if (this.interaction?.selectedSound) {
            this.interaction.selectedSound.currentTime = 0
            this.interaction.selectedSound.play().catch(() => { })
        }

        if (this.linkGroup) {
            gsap.to(this.linkGroup.scale, {
                x: 0.9,
                y: 0.9,
                duration: 0.08,
                yoyo: true,
                repeat: 1,
                ease: "power1.inOut",
                onComplete: () => {
                    if (this.isHovered) {
                        this.linkGroup.scale.set(1.08, 1.08, 1)
                    } else {
                        this.linkGroup.scale.set(1, 1, 1)
                    }
                }
            })
        }

        this.openLink()
    }

    openLink() {
        if (!this.pageLink) return
        try {
            const opened = window.open(this.pageLink, '_blank', 'noopener,noreferrer')
            if (!opened && window.top) {
                window.top.open(this.pageLink, '_blank', 'noopener,noreferrer')
            }
        } catch (e) {
            window.open(this.pageLink, '_blank')
        }
    }

    title() {
        this.titleMesh = this.setText(
            this.pageTitle,
            1.5,
            this.primaryColor,
            new THREE.Vector3(-10, 7, 0)
        )
    }

    wrapText(value, maxCharactersPerLine = this.descriptionMaxCharactersPerLine) {
        if (!value) {
            return ""
        }

        const paragraphs = value.split("\n")
        const formattedParagraphs = []

        for (const paragraph of paragraphs) {
            if (!paragraph) {
                formattedParagraphs.push("")
                continue
            }

            const words = paragraph.split(" ")
            const lines = []
            let currentLine = ""

            for (const word of words) {
                const nextLine = currentLine ? `${currentLine} ${word}` : word

                if (nextLine.length > maxCharactersPerLine && currentLine) {
                    lines.push(currentLine)
                    currentLine = word
                }
                else {
                    currentLine = nextLine
                }
            }

            if (currentLine) {
                lines.push(currentLine)
            }

            formattedParagraphs.push(lines.join("\n"))
        }

        return formattedParagraphs.join("\n")
    }

    description() {
        this.detail = this.setText(
            this.wrapText(this.pageDescription),
            0.55,
            "#c8c8c8",
            new THREE.Vector3(-10, 5, 0)
        )
        this.detail.userData.maxCharactersPerLine = this.descriptionMaxCharactersPerLine
    }

    imagesSetup() {
        this.foregroundPlane = new THREE.Mesh(
            new THREE.PlaneGeometry(9, 9),
            new THREE.MeshBasicMaterial({
                color: this.primaryColor,
            })
        )
        this.imagePlane = new THREE.Mesh(
            new THREE.PlaneGeometry(8.8, 8.8),
            new THREE.MeshBasicMaterial({
                map: this.images,
                transparent: true,
                opacity: 1,
                toneMapped: false
            })
        )
        this.imagePlane.position.z = 0.01

        this.mainImage = new THREE.Group()
        this.mainImage.add(this.imagePlane, this.foregroundPlane)
        this.mainImage.position.set(6, 0, 0)
        this.contentGroup.add(this.mainImage)
        this.objects.push(this.foregroundPlane, this.imagePlane, this.mainImage)
    }

    setText(value, fontSize, fontColor, textPosition, center = false) {
        const geometry = new TextGeometry(value, {
            font: this.font,
            size: fontSize,
            depth: 0,
            curveSegments: 12
        })

        if (center) {
            geometry.center()
        }

        const text = new THREE.Mesh(
            geometry,
            new THREE.MeshBasicMaterial({
                color: fontColor
            })
        )
        text.position.copy(textPosition)
        this.contentGroup.add(text)
        this.objects.push(text)
        text.text = value
        return text
    }

    destroy() {
        if (this.wheelHandler) {
            window.removeEventListener('wheel', this.wheelHandler)
        }
        if (this.pointerDownHandler) {
            window.removeEventListener('pointerdown', this.pointerDownHandler)
        }
        if (this.clickHandler) {
            window.removeEventListener('click', this.clickHandler)
        }
        if (this.pointerMoveHandler) {
            window.removeEventListener('pointermove', this.pointerMoveHandler)
        }
        const canvas = this.home.canvas || this.home.renderer?.instance?.domElement || document.querySelector("canvas.screenWebgl")
        if (canvas) canvas.style.cursor = ''
        document.body.style.cursor = ''

        if (this.linkGroup) {
            gsap.killTweensOf(this.linkGroup.scale)
        }

        if (this.interaction) {
            this.interaction.destroy()
        }
        for (const object of this.objects) {
            if (object.parent) {
                object.parent.remove(object)
            } else {
                this.scene.remove(object)
            }
            if (object.geometry) object.geometry.dispose()
            if (object.material) {
                if (Array.isArray(object.material)) {
                    for (const material of object.material) material.dispose()
                } else {
                    object.material.dispose()
                }
            }
        }
        this.scene.remove(this.contentGroup)
    }
}